import React, { useState, useEffect } from 'react';
import { RootState } from '../Redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';
import axios from 'axios';
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import 'bootstrap/dist/css/bootstrap.min.css';
import { Modal, Button, Card, Breadcrumb, Pagination } from 'react-bootstrap';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './purchase.css';

interface Purchase {
    id: number;
    product: number;
    supplier: number;
    quantity: number;
    purchase_price: string;
    date_purchased: string;
    total_cost: string;
    product_name: string;
    supplier_name: string;
    payment_type: string;
    status: string;
    amount_paid: string;
    balance: string;
    reference_number: string;
}

export default function PurchaseReport() {
    const [purchases, setPurchases] = useState<Purchase[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>(''); // State for search term
    const [userRole, setUserRole] = useState<string>(''); // State for user role

    const dispatch = useDispatch();
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const getCookie = (name: string) => {
        const cookieName = name + '=';
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    };

    const fetchData = async () => {
        try {
            const token = getCookie('token');
            if (!token) {
                dispatch(setAuth(false));
                return;
            }
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            const userResponse = await axios.get('https://api.sominnovations.xyz/users/user', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setUserRole(userResponse.data.user.role); // Set the user role
            dispatch(setAuth(true));
        } catch (e) {
            dispatch(setAuth(false));
        }
    };

    const fetchPurchases = async (page: number) => {
        setLoading(true);
        try {
            const response = await axios.get(`https://api.sominnovations.xyz/Branches/purchase/?page=${page}&search=${searchTerm}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            setPurchases(response.data.results); // Assuming results is an array
            setTotalPages(Math.ceil(response.data.count / 20)); // Assuming 20 items per page
        } catch (error) {
            console.error('Error fetching purchases:', error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [dispatch]);

    useEffect(() => {
        if (auth) {
            fetchPurchases(currentPage);
        }
    }, [auth, currentPage, searchTerm]);

    // Function to download as PDF
    const downloadPDF = () => {
        const doc = new jsPDF();
        doc.text('Purchases Report', 14, 16);
        doc.autoTable({
            head: [['Reference', 'Supplier', 'Product', 'Status', 'Total Cost', 'Payment Type']],
            body: purchases.map(purchase => [
                purchase.reference_number,
                purchase.supplier_name,
                purchase.product_name,
                purchase.status,
                purchase.total_cost,
                purchase.payment_type.charAt(0).toUpperCase() + purchase.payment_type.slice(1),
            ]),
        });
        doc.save('purchase_report.pdf');
    };

    // Function to download as Excel
    const downloadExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(
            purchases.map(purchase => ({
                Reference: purchase.reference_number,
                Supplier: purchase.supplier_name,
                Product: purchase.product_name,
                Status: purchase.status,
                'Total Cost': purchase.total_cost,
                'Payment Type': purchase.payment_type.charAt(0).toUpperCase() + purchase.payment_type.slice(1),
            }))
        );
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchases');
        XLSX.writeFile(workbook, 'purchase_report.xlsx');
    };

    // Function to print
    const handlePrint = () => {
        window.print();
    };

    // If the user is not an admin, do not render the component
    if (userRole !== 'admin') {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger" role="alert">
                    You do not have permission to access this page.
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-1 purchase-product">
            <Card className="mb-4">
                <Card.Header style={{ backgroundColor: '#5271ff', color: '#fff' }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <h4>{t.purchases}</h4>
                        <Breadcrumb>
                            <Breadcrumb.Item href="dashboard">{t.dashboard}</Breadcrumb.Item>
                            <Breadcrumb.Item active>{t.purchases}</Breadcrumb.Item>
                        </Breadcrumb>
                    </div>
                </Card.Header>
                <Card.Body>
                    <Button variant="info" className="ms-2" onClick={downloadPDF}>
                        {t.downloadPDF}
                    </Button>
                    <Button variant="success" className="ms-2" onClick={downloadExcel}>
                        {t.downloadExcel}
                    </Button>
                    <Button variant="primary" className="ms-2" onClick={handlePrint}>
                        {t.print}
                    </Button>
                </Card.Body>
            </Card>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <>
                    <div className="table-responsive-sm">
                        <table className="table table-striped table-responsive table-sm" style={{ fontSize: '14px', margin: 'auto' }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>{t.salesDetails}</th>
                                    <th>{t.suppliers}</th>
                                    <th>{t.products}</th>
                                    <th>{t.status}</th>
                                    <th>{t.grandTotal}</th>
                                    <th>{t.paymentType}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {purchases.map((purchase) => (
                                    <tr key={purchase.reference_number}>
                                        <td>{purchase.reference_number}</td>
                                        <td>{purchase.supplier_name}</td>
                                        <td>{purchase.product_name}</td>
                                        <td>{purchase.status}</td>
                                        <td>{purchase.total_cost}</td>
                                        <td>{purchase.payment_type.charAt(0).toUpperCase() + purchase.payment_type.slice(1)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    <Pagination>
                        {Array.from({ length: totalPages }).map((_, i) => (
                            <Pagination.Item key={i} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                                {i + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </>
            )}
        </div>
    );
}